// Portuguese tax figures, one set per year (mainland Portugal). When a year isn't here yet, the latest known year is
// used and everything computed with it is marked as an estimate. Newer years can be added from Settings → Tax rules
// (stored with the data) without changing the code.
//
// 2026 sources:
//   Withholding tables: Despacho n.º 233-A/2026 (Diário da República, 6 Jan 2026), Tabelas I–III
//   Brackets: Lei n.º 73-A/2025 (OE 2026), art. 68.º CIRS, with the first six rates cut for 2026 by the decree-law
//   authorised by Lei n.º 64-B/2026 (22 Oct 2026) · Mínimo de existência: art. 70.º (12 880 €)
//   November 2026 withholding: Despacho n.º 12523-A/2026 — only 50% is withheld, except above the penultimate row
//   IAS 2026: 537,13 € (Portaria n.º 480-A/2025/1) · IRS Jovem: art. 12.º-B and 99.º-F CIRS
//   Meal allowance exempt limits: 6,15 €/day in cash, 10,46 €/day on a meal card (art. 2.º, n.º 3, b) 2) CIRS)

// A withholding row: up to a monthly remuneration, a marginal rate (%), and the amount to subtract. The subtracted
// amount is either a fixed number or a formula `rate × k × (ref − R)` used just above the minimum wage.
const row = (upTo, rate, deduct) => ({ upTo, rate, deduct });
const formula = (k, ref) => ({ k, ref });

const TABLE_I_2026 = [
  row(920, 0, 0),
  row(1042, 12.5, formula(2.6, 1273.85)),
  row(1108, 15.7, formula(1.35, 1554.83)),
  row(1154, 15.7, 94.71),
  row(1212, 21.2, 158.18),
  row(1819, 24.1, 193.33),
  row(2119, 31.1, 320.66),
  row(2499, 34.9, 401.19),
  row(3305, 38.36, 487.66),
  row(5547, 39.69, 531.62),
  row(20221, 44.95, 823.4),
  row(null, 47.17, 1272.31),
];

const TABLE_III_2026 = [
  row(991, 0, 0),
  row(1042, 12.5, formula(2.6, 1372.15)),
  row(1108, 12.5, formula(1.35, 1677.85)),
  row(1119, 12.5, 96.17),
  row(1432, 12.72, 98.64),
  row(1962, 15.7, 141.32),
  row(2240, 19.38, 213.53),
  row(2773, 22.77, 289.47),
  row(3389, 25.7, 370.72),
  row(5965, 28.81, 476.12),
  row(20265, 38.43, 1049.96),
  row(null, 47.17, 2821.13),
];

export const BUILTIN_YEARS = {
  2026: {
    year: 2026,
    source: 'Despacho 233-A/2026 (+ 12523-A/2026 for November) · OE 2026 · IRS cut of Lei 64-B/2026',
    ias: 537.13,
    minWage: 920,
    // art. 68.º: taxable income brackets (annual), normal rate and the law's average rate at each upper limit (%)
    brackets: [
      // Rates of the first six brackets cut for all of 2026 income by Lei 64-B/2026 (October 2026); limits unchanged
      { upTo: 8342, rate: 12.2, avg: 12.2 },
      { upTo: 12587, rate: 15.2, avg: 13.212 },
      { upTo: 17838, rate: 20.7, avg: 15.416 },
      { upTo: 23089, rate: 23.6, avg: 17.277 },
      { upTo: 29397, rate: 30.6, avg: 20.136 },
      { upTo: 43090, rate: 34.6, avg: 24.732 },
      { upTo: 46566, rate: 43.1, avg: 26.103 },
      { upTo: 86634, rate: 44.6, avg: 34.658 },
      { upTo: null, rate: 48 },
    ],
    // art. 25.º: employment income deduction = the larger of 4 104 €, 8,54 × IAS, or the social security paid
    specificDeduction: { minimum: 4104, iasMultiple: 8.54 },
    minimoExistencia: 12880,
    socialSecurity: { employee: 11, employer: 23.75 },
    mealExempt: { card: 10.46, cash: 6.15 },
    irsJovem: { percentages: [100, 75, 75, 75, 50, 50, 50, 25, 25, 25], capIas: 55, maxAge: 35 },
    // One-off changes to withholding in a month: only `factor` of the normal amount is withheld, unless the payment
    // is above the limit of the second-to-last row of its table
    withholdingAdjustments: [{ month: '2026-11', factor: 0.5, exceptAbovePenultimate: true }],
    withholding: {
      // I: single without dependants, or married with both earning · II: single with dependants · III: married, sole earner
      I: { rows: TABLE_I_2026, perDependent: 21.43 },
      II: { rows: TABLE_I_2026, perDependent: 34.29 },
      III: { rows: TABLE_III_2026, perDependent: 42.86 },
    },
  },
};

export const TABLE_NAMES = {
  I: 'Single, no dependants (or married, both earning)',
  II: 'Single with dependants',
  III: 'Married, only one earning',
};

const latestBuiltin = () => Math.max(...Object.keys(BUILTIN_YEARS).map(Number));

// The figures for a year: { params, year, sourceYear, estimated }
export function taxParamsFor(year, overrides = {}) {
  const own = { ...BUILTIN_YEARS, ...(overrides || {}) };
  if (own[year]) return { params: own[year], year, sourceYear: year, estimated: false };
  const known = Object.keys(own).map(Number).filter((y) => Number.isFinite(y)).sort((a, b) => a - b);
  const before = known.filter((y) => y < year);
  const sourceYear = before.length ? before[before.length - 1] : known[0] ?? latestBuiltin();
  return { params: own[sourceYear] || BUILTIN_YEARS[latestBuiltin()], year, sourceYear, estimated: true };
}

export const irsJovemCap = (p) => Math.round(p.irsJovem.capIas * p.ias * 100) / 100;
export const specificDeductionAmount = (p) => Math.max(p.specificDeduction.minimum, Math.round(p.specificDeduction.iasMultiple * p.ias * 100) / 100);

// Basic sanity checks for figures typed in Settings; returns a list of problems (empty when fine)
export function validateParams(p) {
  const errs = [];
  const num = (v) => typeof v === 'number' && Number.isFinite(v);
  if (!p || typeof p !== 'object') return ['Missing figures'];
  if (!num(p.ias) || p.ias <= 0) errs.push('IAS must be a positive amount');
  if (!Array.isArray(p.brackets) || !p.brackets.length) errs.push('Add at least one tax bracket');
  else {
    let prev = 0;
    p.brackets.forEach((b, i) => {
      const last = i === p.brackets.length - 1;
      if (!num(b.rate) || b.rate < 0 || b.rate > 100) errs.push(`Bracket ${i + 1}: rate must be between 0 and 100`);
      if (!last && (!num(b.upTo) || b.upTo <= prev)) errs.push(`Bracket ${i + 1}: limit must be higher than the one before`);
      if (last && b.upTo !== null) errs.push('The last bracket must have no upper limit');
      if (num(b.upTo)) prev = b.upTo;
    });
  }
  for (const t of ['I', 'II', 'III']) {
    const tab = p.withholding?.[t];
    if (!tab || !Array.isArray(tab.rows) || !tab.rows.length) { errs.push(`Withholding table ${t} is missing`); continue; }
    let prev = 0;
    tab.rows.forEach((r, i) => {
      const last = i === tab.rows.length - 1;
      if (!num(r.rate) || r.rate < 0 || r.rate > 100) errs.push(`Table ${t}, row ${i + 1}: rate must be between 0 and 100`);
      if (!last && (!num(r.upTo) || r.upTo <= prev)) errs.push(`Table ${t}, row ${i + 1}: limit must be higher than the one before`);
      if (last && r.upTo !== null) errs.push(`Table ${t}: the last row must have no upper limit`);
      if (num(r.upTo)) prev = r.upTo;
      const d = r.deduct;
      if (!(num(d) || (d && num(d.k) && num(d.ref)))) errs.push(`Table ${t}, row ${i + 1}: amount to subtract is missing`);
    });
  }
  if (!num(p.mealExempt?.card) || !num(p.mealExempt?.cash)) errs.push('Meal allowance limits are missing');
  if (!num(p.socialSecurity?.employee)) errs.push('Social security rate is missing');
  if (!Array.isArray(p.irsJovem?.percentages) || p.irsJovem.percentages.length !== 10) errs.push('IRS Jovem needs 10 yearly percentages');
  return errs;
}
